import { useEffect, useRef, useCallback } from 'react';

// Global WebSocket connection to prevent multiple connections
let globalCable = null;
let globalReconnectTimeout = null;
let globalReconnectAttempts = 0;
const maxReconnectAttempts = 5;

export function useNotificationSocket({ onMessage }) {
  const onMessageRef = useRef(onMessage);
  
  // Update the message handler reference
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(() => {
    if (globalCable?.readyState === WebSocket.OPEN || globalCable?.readyState === WebSocket.CONNECTING) {
      return; // Already connected or connecting
    }

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const cable = new WebSocket(`${protocol}//${window.location.host}/cable`);
      globalCable = cable;

      cable.onopen = () => {
        globalReconnectAttempts = 0;
        
        const subscribeMessage = {
          command: 'subscribe',
          identifier: JSON.stringify({ channel: 'NotificationChannel' })
        };
        cable.send(JSON.stringify(subscribeMessage));
      };

      cable.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle ActionCable protocol messages
          if (data.type === 'ping') {
            return;
          }
          
          if (data.type === 'confirm_subscription') {
            return;
          }

          // Handle actual notifications
          if (data.message && onMessageRef.current) {
            onMessageRef.current(data.message);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      cable.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      cable.onclose = (event) => {
        console.log('WebSocket connection closed:', event.code, event.reason);
        
        // Attempt to reconnect if not a clean close
        if (event.code !== 1000 && globalReconnectAttempts < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, globalReconnectAttempts), 10000);
          globalReconnectAttempts++;
          
          console.log(`Attempting to reconnect in ${delay}ms (attempt ${globalReconnectAttempts}/${maxReconnectAttempts})`);
          
          globalReconnectTimeout = setTimeout(() => {
            connect();
          }, delay);
        } else if (globalReconnectAttempts >= maxReconnectAttempts) {
          console.log('Max reconnection attempts reached. WebSocket will not reconnect.');
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [onMessage]);

  useEffect(() => {
    // Only connect if not already connected
    if (!globalCable || globalCable.readyState === WebSocket.CLOSED) {
      connect();
    }

    return () => {
      // Don't clear global timeout or close connection on unmount
      // The connection should persist across component mounts/unmounts
    };
  }, []); // Empty dependency array to run only once

  return globalCable;
}
